import React, { useState, useEffect, useRef } from "react";
import { Table, Button, Space } from "antd";
import "./bundleTransport.scss";
import {
  GoogleMap,
  Marker,
  Polyline,
  OverlayView,
} from "@react-google-maps/api";
import { ToastContainer, toast } from "react-toastify";
import decodePolyline from "decode-google-map-polyline";
import origin from "../../assets/origin.png";
import destination from "../../assets/destination.png";
import { log, getMapIdBasedonEnvironment } from "../../helper/helper";
import * as service from "./bundleTransportServices";
import Spinner from "../common/spinner";
import { getAuth, onAuthStateChanged } from "firebase/auth";

function SortBundleTransport(props) {
  const [data, setData] = useState([]);
  const [positions, setPositions] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [map, setMap] = useState(null);
  const [fadingRow, setFadingRow] = useState(null);
  const transportListToBundle = props.location.state.transportListToBundle;

  useEffect(() => {
    const auth = getAuth();
    onAuthStateChanged(auth, (user) => {
      if (!user) {
        return props.history.replace("/login");
      }
    });

    if (Array.isArray(transportListToBundle)) {
      const formatted = transportListToBundle.flatMap((item) => [
        {
          id: item.transport_id,
          status: "Pick Up",
          order_number: item.order_number,
          address: item.origin_address,
        },
        {
          id: item.transport_id,
          status: "Drop Off",
          order_number: item.order_number,
          address: item.destination_address,
        },
      ]);
      setData(formatted);
    }

    const positionsData = [];

    for (const trip of transportListToBundle) {
      positionsData.push(
        {
          lat: trip.origin_geopoint._latitude,
          lng: trip.origin_geopoint._longitude,
          icon: origin,
          zIndex: 9,
        },
        {
          lat: trip.destination_geopoint._latitude,
          lng: trip.destination_geopoint._longitude,
          icon: destination,
          zIndex: 9,
        }
      );
    }

    setPositions(positionsData);
  }, [transportListToBundle]);

  const idColorMapRef = useRef({});
  const colorPalette = [
    "#006ecf3f",
    "#ff110033",
    "#7b00ff2b",
    "#00ff3346",
    "#8600e03f",
    "#f0290634",
  ];

  const onLoadBounds = (map) => {
    if (!map) return;
    const bounds = new window.google.maps.LatLngBounds();
    positions.forEach((child) => {
      bounds.extend(new window.google.maps.LatLng(child.lat, child.lng));
    });
    map.fitBounds(bounds);
    setMap(map);
  };

  const defaultMapOptions = {
    fullscreenControl: true,
  };

  const onUnmount = React.useCallback(() => {
    setMap(null);
  }, []);

  const containerStyle = {
    width: "100%",
    height: "50vh",
  };

  const options = {
    fillColor: "lightblue",
    fillOpacity: 1,
    strokeColor: "blue",
    strokeOpacity: 1,
    strokeWeight: 2,
    clickable: false,
    draggable: false,
    editable: false,
    geodesic: false,
    zIndex: 1,
  };

  function generateCurvePoints(start, end, curvature = 0.2, segments = 30) {
    const lat1 = start.lat;
    const lng1 = start.lng;
    const lat2 = end.lat;
    const lng2 = end.lng;

    const dLat = lat2 - lat1;
    const dLng = lng2 - lng1;

    const controlLat = lat1 + dLat / 2 + curvature * dLng;
    const controlLng = lng1 + dLng / 2 - curvature * dLat;

    const curvePoints = [];
    for (let t = 0; t <= 1; t += 1 / segments) {
      const lat =
        Math.pow(1 - t, 2) * lat1 +
        2 * (1 - t) * t * controlLat +
        Math.pow(t, 2) * lat2;
      const lng =
        Math.pow(1 - t, 2) * lng1 +
        2 * (1 - t) * t * controlLng +
        Math.pow(t, 2) * lng2;
      curvePoints.push({ lat, lng });
    }
    return curvePoints;
  }

  const loadGoogleMap = () => {
    const orderedPositions = data.map((item) => {
      const trip = transportListToBundle.find(
        (t) => t.transport_id === item.id
      );
      return item.status === "Pick Up"
        ? {
            lat: trip.origin_geopoint._latitude,
            lng: trip.origin_geopoint._longitude,
            icon: origin,
            type: "Pick Up",
          }
        : {
            lat: trip.destination_geopoint._latitude,
            lng: trip.destination_geopoint._longitude,
            icon: destination,
            type: "Drop Off",
          };
    });

    return (
      <GoogleMap
        mapContainerStyle={containerStyle}
        zoom={13}
        onLoad={onLoadBounds}
        onUnmount={onUnmount}
        defaultOptions={defaultMapOptions}
        options={{
          mapId: getMapIdBasedonEnvironment(),
        }}
      >
        {orderedPositions.map((position, index) => {
          const circleColor =
            position.type === "Pick Up" ? "#28a745" : "#dc3545";

          return (
            <React.Fragment key={`${position.lat}-${position.lng}-${index}`}>
              <Marker
                position={position}
                icon={{
                  url: position.icon,
                  scaledSize: new window.google.maps.Size(25, 40),
                }}
              />
              <OverlayView
                position={position}
                mapPaneName={"overlayMouseTarget"}
              >
                <div
                  style={{
                    transform: "translate(-50%, 10px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: circleColor,
                    color: "white",
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    fontSize: "12px",
                    fontWeight: "bold",
                    border: "2px solid white",
                    boxShadow: "0 0 3px rgba(0,0,0,0.3)",
                    userSelect: "none",
                  }}
                >
                  {index + 1}
                </div>
              </OverlayView>
            </React.Fragment>
          );
        })}

        {orderedPositions.length > 1 &&
          orderedPositions.slice(0, -1).map((point, i) => {
            const nextPoint = orderedPositions[i + 1];
            const curvePath = generateCurvePoints(point, nextPoint);

            return (
              <Polyline
                key={`curve-${i}`}
                path={curvePath}
                options={{
                  strokeColor: "#d45b10ff",
                  strokeOpacity: 0.2,
                  strokeWeight: 0.2,
                  icons: [
                    {
                      icon: {
                        path: "M 0,-1 0,1",
                        strokeOpacity: 1,
                        scale: 2,
                      },
                      offset: "0",
                      repeat: "10px",
                    },
                  ],
                  geodesic: true,
                  clickable: false,
                }}
              />
            );
          })}

        {transportListToBundle.map((trip, idx) => (
          <Polyline
            key={`polyline-${idx}`}
            path={decodePolyline(trip.overview_polyline)}
            options={options}
          />
        ))}
      </GoogleMap>
    );
  };

  const getColorForId = (id) => {
    if (!idColorMapRef.current[id]) {
      const usedColors = Object.values(idColorMapRef.current);
      const availableColor =
        colorPalette[usedColors.length % colorPalette.length];
      idColorMapRef.current[id] = availableColor;
    }
    return idColorMapRef.current[id];
  };

  const moveRow = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= data.length) return;

    const current = data[index];
    const neighbor = data[newIndex];
    if (!neighbor) return;
    if (
      (current.status === "Pick Up" &&
        neighbor.status === "Drop Off" &&
        current.id === neighbor.id) ||
      (current.status === "Drop Off" &&
        neighbor.status === "Pick Up" &&
        current.id === neighbor.id)
    ) {
      return;
    }
    setFadingRow(index);
    setTimeout(() => {
      const newData = [...data];
      [newData[index], newData[newIndex]] = [newData[newIndex], newData[index]];
      setData(newData);
      setFadingRow(null);
    }, 200);
  };

  const isMoveDisabled = (record, index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= data.length) return true;

    const neighbor = data[newIndex];
    if (!neighbor) return true;
    if (
      (record.status === "Pick Up" &&
        neighbor.status === "Drop Off" &&
        record.id === neighbor.id) ||
      (record.status === "Drop Off" &&
        neighbor.status === "Pick Up" &&
        record.id === neighbor.id)
    ) {
      return true;
    }
    return false;
  };

  const columns = [
    { title: "Order Number", dataIndex: "order_number", key: "order_number" },
    { title: "Task", dataIndex: "status", key: "status" },
    { title: "Address", dataIndex: "address", key: "address" },
    {
      title: "Task Order",
      key: "actions",
      render: (_, record, index) => (
        <Space>
          <Button
            onClick={() => moveRow(index, -1)}
            disabled={isMoveDisabled(record, index, -1)}
          >
            ↑
          </Button>
          <Button
            onClick={() => moveRow(index, 1)}
            disabled={isMoveDisabled(record, index, 1)}
          >
            ↓
          </Button>
        </Space>
      ),
    },
  ];

  const handleBundleIds = async () => {
    const transportIds = data.map((item) => [
      item.id,
      item.status === "Pick Up"
        ? "pickup"
        : item.status === "Drop Off"
        ? "dropOff"
        : item.status,
    ]);
    setLoading(true);
    try {
      const result = await service.adminArrangementBundledTransports(
        transportIds
      );
      log(result);
      if (result) {
        setLoading(false);
        switch (result.data.code) {
          case 200:
            toast.success(result.data.message);
            return props.history.replace(
              "/home/bundleTransport/lockedBundleTransport/bundleTransportSingle",
              {
                data: result.data,
              }
            );
          case 401:
            toast.error(result.data.message);
            return props.history.replace("/login");
          case 500:
            return toast.error(result.data.message);
          default:
            return toast.error(result.data.message);
        }
      }
    } catch (error) {
      log(error);
      toast.error(error.message);
    }
    setLoading(false);
  };

  const cancelBundleIds = async () => {
    setLoading(true);
    const ids = [...new Set(data.map((item) => item.id))];
    try {
      const result = await service.adminUnlockTransportForCreateBundled(ids);
      log(result);
      if (result) {
        setLoading(false);
        switch (result.data.code) {
          case 200:
            props.history.goBack();
            return;
          case 401:
            toast.error(result.data.message);
            return props.history.replace("/login");
          case 500:
            return toast.error(result.data.message);
          default:
            return toast.error(result.data.message);
        }
      }
    } catch (error) {
      log(error);
      toast.error(error.message);
    }
    setLoading(false);
  };

  const renderSpinner = () => (isLoading ? <Spinner /> : null);

  return (
    <div className="table-design car-list-history show-all-list">
      <ToastContainer />
      {renderSpinner()}
      <div className="row">
        {positions.length > 0 && <div className="map">{loadGoogleMap()}</div>}
        <Table
          className="animated-table no-hover"
          columns={columns}
          dataSource={data.map((item, i) => ({
            ...item,
            key: i,
            isFading: fadingRow === i,
          }))}
          pagination={false}
          rowClassName={(record) => (record.isFading ? "fade-row" : "")}
          onRow={(record) => ({
            style: {
              backgroundColor: getColorForId(record.id),
              transition: "background-color 0.3s ease, opacity 0.3s ease",
            },
          })}
        />
      </div>
      <div className="load-more">
        <ul style={{ margin: "20px 0" }}>
          <li style={{ display: "inline-block" }}>
            <button onClick={() => cancelBundleIds()}>Cancel & Unlock</button>
          </li>
          <li style={{ display: "inline-block", margin: "20px" }}>
            <button onClick={() => handleBundleIds()}>
              Confirm & Calculate
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
}

export default SortBundleTransport;
